import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux"
import { selectTranslations } from "../../../App/i18n/i18nSlice"
import ModalGroup from "../../Shared/Components/ModalGroup"
import { BackIcon, EventsTabIcon, MediaTabIcon, MembersTabIcon, PostsCommentsTabIcon, PostsTabIcon } from "../../Shared/Components/SvgIcons"
import Header from "../../Shared/Header/Header"
import Footer from "../../Shared/Layout/Footer"
import RoomSinglePostExpanded from "../RoomPost/RoomSinglePostExpanded"
import RoomHeader from "../Components/RoomHeader"
import { useNavigate, useParams, useSearchParams } from "react-router"
import { setRoomData, setRoomManagerData } from "../../Shared/SharedSlices/RoomSlice"
import { RootState } from "../../../App/store"
import { getRoomRequest } from "../RoomServiceRequests/RoomServiceRequests"
import { JoinStatus, PostType, RoomType } from "../../../App/enums"
import { headers } from "../../../App/Axios"
import HTTPService from "../../../App/HTTPService"
import { setIsMember, setPendingType } from "../RoomSlices/MemberInRoomStatusSlice"
import { capitalizeFirstLetter } from "../../../App/Helpers/Helpers"
import BackgroundWrapper from "../../Shared/Components/BackgroundWrapper"
import RoomTextArea from "../Components/RoomTextArea"
import RoomInformation from "./RoomInformation"
import { onBackToRoomNavigation } from "../RoomPost/onNavigation"
import { Tab, TabList, TabPanel, Tabs } from "react-tabs"
import RoomMembers from "../Components/RoomMembers"
import MediaLibrary from "./MediaLibrary"
import RoomEvents from "./RoomEvents"
import RoomPostTab from "./RoomPostTab/RoomPostTab";
import MobileBottomNav from "../../Shared/Layout/MobileBottomNav";
import RoomComposerFab from "../Components/RoomComposerFab"
import useWindowSize from "../../Shared/Hooks/useWindowSize"

function RoomScreen() {
    const translation = useSelector(selectTranslations)
    const [searchParams, setSearchParams] = useSearchParams()
    const tabParam = searchParams.get('tab') || 'posts'
    const searchParamsString = searchParams.toString()

    const { roomId, id } = useParams()
    const roomDataState = useSelector((state: RootState) => state.roomSlice.roomSlice.room)
    const dispatch = useDispatch()
    const allow_requests = roomDataState?.room_permissions && roomDataState?.room_permissions[0]?.allow_requests!
    const invite_others = roomDataState?.room_permissions && roomDataState?.room_permissions[0]?.invite_others!
    const share_documents = roomDataState?.room_permissions && roomDataState?.room_permissions[0]?.share_documents!
    const only_admins_can_upload = roomDataState?.room_permissions && roomDataState?.room_permissions[0]?.only_admins_can_upload!
    const userDataState = useSelector((state: RootState) => state.userDataSlice)
    const isMemberInRoom = useSelector((state: RootState) => state.MemberInRoomStatusSlice.MemberInRoomStatusSlice.isMember)
    const canSeePosts = roomDataState.type === RoomType.Private ? isMemberInRoom === JoinStatus.MEMBER : true
    const role = useSelector((state: RootState) => state.rolesSlice.RolesSlice.roles)
    const navigate = useNavigate()
    const isRoomManager = useSelector((state: RootState) => state.roomSlice.roomManagerSlice.room_manager)
    const screenWidth = useWindowSize()
    const isMobile = screenWidth < 768
    const tabIndex =
        tabParam === 'members'
            ? 1
            : tabParam === 'new-post'
                ? 2
                : tabParam === 'media'
                    ? 3
                    : tabParam === 'events'
                        ? 4
                        : 0
    const [showComposer, setShowComposer] = useState(false);

    useEffect(() => {
        const updated = new URLSearchParams(searchParamsString)
        switch (tabParam) {
            case 'posts':
                updated.delete('file')
                updated.delete('event')
                break
            case 'members':
                updated.delete('file')
                updated.delete('event')
                break
            case 'new-post':
                updated.delete('file')
                break
            case 'media':
                updated.delete('event')
                break
            case 'events':
                updated.delete('file')
                break
            default:
                updated.delete('file')
                updated.delete('event')
        }

        if (updated.toString() !== searchParamsString) {
            setSearchParams(updated, { replace: true })
        }
    }, [tabParam, searchParamsString, setSearchParams])

    useEffect(() => {
        let isMounted = true
        getRoomRequest(parseInt(roomId!))
            .then(response => {
                if (isMounted && response.status === 200) {
                    dispatch(setRoomData(response.data))
                }
            })
            .catch(error => {
                console.log(error)
            })

        return () => {
            isMounted = false
        }
    }, [
        dispatch,
        roomId,
        roomDataState?.name,
        roomDataState?.photo,
        roomDataState?.type,
        allow_requests,
        only_admins_can_upload,
        invite_others,
        share_documents,
        roomDataState?.description,
        navigate,
    ])

    const getUserStatus = useCallback(() => {
        return HTTPService.get(`/user-status-in-room/${roomId}/`, headers)
    }, [roomId])

    useEffect(() => {
        getUserStatus().then(response => {
            if (response.status === 200) {
                dispatch(setIsMember(response.data.status))
                dispatch(setPendingType({ type: response.data.type, notification_id: response.data.notification_id }))
            }
        })
    }, [dispatch, getUserStatus])

    useEffect(() => {
        dispatch(setRoomManagerData(roomDataState?.room_managers?.some(manager => manager.id === userDataState.id)!))
    }, [dispatch, roomDataState?.room_managers, roomId, userDataState.id])

    return (
        <BackgroundWrapper>
            <Header roomHeader={true} />
            <div className="room-screen">
                <div className="room-layout">
                    <RoomInformation isMemberInRoom={isMemberInRoom} hasPrivelegedPermission={isRoomManager} />
                    <div className="room-layout__right-panel">
                        <div
                            className={`room-layout__right-panel-header ${(isMemberInRoom === JoinStatus.NON_MEMBER || JoinStatus.PENDING) && "room-layout__right-panel-header--join-button"
                                }`}
                        >
                            <RoomHeader
                                roomType={capitalizeFirstLetter(roomDataState?.type! === RoomType.Private ? translation.private : translation.public)}
                            />
                        </div>
                        <Tabs
                            className="tabs-container"
                            selectedIndex={tabIndex}
                            onSelect={(index) => {
                                const nextTab =
                                    index === 1
                                        ? 'members'
                                        : index === 2
                                            ? 'new-post'
                                            : index === 3
                                                ? 'media'
                                                : index === 4
                                                    ? 'events'
                                                    : 'posts'

                                const updated = new URLSearchParams(searchParamsString)
                                updated.set('tab', nextTab)
                                if (nextTab === 'posts' || nextTab === 'members') {
                                    updated.delete('file')
                                    updated.delete('event')
                                } else if (nextTab === 'new-post') {
                                    updated.delete('file')
                                } else if (nextTab === 'media') {
                                    updated.delete('event')
                                } else if (nextTab === 'events') {
                                    updated.delete('file')
                                }

                                if (updated.toString() !== searchParamsString) {
                                    setSearchParams(updated, { replace: true })
                                }
                            }}
                        >
                            <TabPanel>
                                <div className="room-layout__right-panel-content">
                                    {roomDataState.type && !canSeePosts ? (
                                        <div className="room-layout__no-access">{translation.cantSeeRoomContent}</div>
                                    ) : (
                                        <>
                                            <div className="room-layout__back-container">
                                                <div className="room-layout__back-icon-container"
                                                    onClick={() => onBackToRoomNavigation(role, roomId!, id!, navigate)}>
                                                    <div className="room-layout__back-icon">
                                                        <BackIcon />
                                                    </div>
                                                    <div className="room-layout__text">{translation.allPosts}</div>
                                                </div>
                                            </div>
                                            <RoomSinglePostExpanded />
                                        </>
                                    )}
                                </div>
                                {roomDataState.is_active && tabParam !== 'new-post' && !isMobile && (
                                    <RoomTextArea
                                        hasPrivelegedPermission={isRoomManager}
                                        isMemberInRoom={isMemberInRoom}
                                        share_documents={share_documents}
                                        type={PostType.comment}
                                        onClose={() => setShowComposer(false)}
                                    />
                                )}
                                {roomDataState.is_active && tabParam !== 'new-post' && isMobile && (
                                    showComposer ? (
                                        <RoomTextArea
                                            hasPrivelegedPermission={isRoomManager}
                                            isMemberInRoom={isMemberInRoom}
                                            share_documents={share_documents}
                                            type={PostType.comment}
                                            onClose={() => setShowComposer(false)}
                                        />
                                    ) : (
                                        <RoomComposerFab
                                            onCreatePost={() => {
                                                const updated = new URLSearchParams(searchParamsString)
                                                updated.set('tab', 'new-post')
                                                setSearchParams(updated, { replace: true })
                                            }}
                                            onCreateComment={() => setShowComposer(true)}
                                        />
                                    )
                                )}
                            </TabPanel>
                            <TabPanel>
                                <div className="room-layout__right-panel-content room-layout__right-panel-content--member-panel">
                                    <RoomMembers />
                                </div>
                            </TabPanel>
                            <TabPanel>
                                <RoomPostTab />
                            </TabPanel>
                            <TabPanel className={'media-library__tabs-panel'}>
                                <MediaLibrary />
                            </TabPanel>
                            <TabPanel className={'room-events__tabs-panel'}>
                                <RoomEvents />
                            </TabPanel>
                            <MobileBottomNav isRoomMenu={true} />
                            <TabList>
                                <Tab>
                                    <div className="react-tabs__tab-menu-item">
                                        <PostsTabIcon />
                                        <span>Austausch</span>
                                    </div>
                                </Tab>
                                <Tab>
                                    <div className="react-tabs__tab-menu-item">
                                        <MembersTabIcon />
                                        <span>Mitglieder</span>                                    </div>

                                </Tab>
                                <Tab>
                                    <div className="react-tabs__tab-menu-item">
                                        <PostsCommentsTabIcon />
                                        <span>Posten</span>
                                    </div>
                                </Tab>
                                <Tab>
                                    <div className="react-tabs__tab-menu-item">
                                        <MediaTabIcon />
                                        <span>Mediathek</span>
                                    </div>
                                </Tab>
                                <Tab>
                                    <div className="react-tabs__tab-menu-item">
                                        <EventsTabIcon />
                                        <span>Events</span>
                                    </div>
                                </Tab>
                            </TabList>
                        </Tabs>
                    </div>
                </div>
            </div>
            <Footer roomfooter={true} />
            <ModalGroup />
        </BackgroundWrapper>
    )
}

export default RoomScreen
